/**
 * Confirm dialog for RTR quick-actions on the Triage alert drawer.
 * Captures the X-Smplify-Confirm-Action reason text.
 */
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export interface RtrConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  deviceName?: string;
  badges?: Array<{ label: string; value: string }>;
  confirmLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: (reason: string) => void;
}

export function RtrConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  deviceName,
  badges = [],
  confirmLabel = "Run command",
  destructive = false,
  loading = false,
  onConfirm,
}: RtrConfirmProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {(deviceName || badges.length > 0) && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-1.5">
            {deviceName && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Device</span>
                <span className="font-mono text-foreground truncate">{deviceName}</span>
              </div>
            )}
            {badges.map((b) => (
              <div key={b.label} className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{b.label}</span>
                <Badge variant="outline" className="font-mono text-[10px] max-w-[60%] truncate">
                  {b.value}
                </Badge>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="rtr-reason" className="text-xs">
            Reason (required) — recorded on the audit trail
          </Label>
          <Textarea
            id="rtr-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Containing alice's laptop pending IR review"
            rows={3}
            className="text-xs"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={loading || reason.trim().length < 3}
            onClick={(e) => {
              e.preventDefault();
              onConfirm(reason.trim());
            }}
            className={
              destructive
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : undefined
            }
          >
            {loading ? "Queuing…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
